import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../components/utils/api';
import ProfileSubviewHeader from '../components/ProfileView/ProfileSubviewHeader';
import './NotificationSettings.scss';

interface NotificationSettingsProps {
  onBack?: () => void;
  /** The profile view draws the header itself; a page mounting this standalone keeps its own. */
  hideHeader?: boolean;
}

interface Settings {
  sms_notification_match: boolean;
  sms_notification_message: boolean;
  sms_notification_price_estimation: boolean;
  sms_notification_property_tip: boolean;
  email_notification_match: boolean;
  email_notification_message: boolean;
  email_notification_price_estimation: boolean;
  email_notification_property_tip: boolean;
  marketing_consent: boolean;
  phone_number?: string | null;
  phone_verified?: boolean;
}

const DEFAULTS: Settings = {
  sms_notification_match: true,
  sms_notification_message: true,
  sms_notification_price_estimation: true,
  sms_notification_property_tip: true,
  email_notification_match: true,
  email_notification_message: true,
  email_notification_price_estimation: true,
  email_notification_property_tip: true,
  marketing_consent: false,
};

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ onBack, hideHeader }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<Settings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/notification-settings', { method: 'GET' });
      if (isMountedRef.current) setSettings({ ...DEFAULTS, ...data });
    } catch {
      // silently use defaults
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleToggle = useCallback(async (field: keyof Settings, value: boolean) => {
    setSuccessMsg(null);
    setErrorMsg(null);
    setSaving(true);
    const prev = settings[field];
    setSettings(s => ({ ...s, [field]: value }));
    try {
      await apiRequest('/notification-settings', {
        method: 'PUT',
        body: { [field]: value },
      });
      if (isMountedRef.current) setSuccessMsg(t('notificationSettings.saved'));
    } catch {
      if (isMountedRef.current) {
        setSettings(s => ({ ...s, [field]: prev }));
        setErrorMsg(t('notificationSettings.errorSaving'));
      }
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [settings, t]);

  const smsAvailable = !!settings.phone_number && !!settings.phone_verified;

  const NOTIFICATION_ROWS: { label: string; smsField?: keyof Settings; emailField: keyof Settings }[] = [
    { label: 'match', smsField: 'sms_notification_match', emailField: 'email_notification_match' },
    { label: 'message', smsField: 'sms_notification_message', emailField: 'email_notification_message' },
    { label: 'priceEstimation', smsField: 'sms_notification_price_estimation', emailField: 'email_notification_price_estimation' },
    { label: 'propertyTip', smsField: 'sms_notification_property_tip', emailField: 'email_notification_property_tip' },
    { label: 'marketingConsent', emailField: 'marketing_consent' },
  ];

  const renderSwitch = (field: keyof Settings, disabled?: boolean) => (
    <label className="notification-settings__switch">
      <input
        type="checkbox"
        checked={!!settings[field]}
        disabled={disabled || saving}
        onChange={(e) => handleToggle(field, e.target.checked)}
      />
      <span className="notification-settings__switch__slider" />
    </label>
  );

  if (loading) {
    return (
      <div className="notification-settings notification-settings--loading">
        {onBack && !hideHeader && (
          <ProfileSubviewHeader title={t('notificationSettings.pageTitle')} onBack={onBack} />
        )}
        <div className="notification-settings__spinner"></div>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      {onBack && !hideHeader && (
        <ProfileSubviewHeader title={t('notificationSettings.pageTitle')} onBack={onBack} />
      )}

      {successMsg && <p className="notification-settings__success">{successMsg}</p>}
      {errorMsg && <p className="notification-settings__error">{errorMsg}</p>}

      {/* Notification table */}
      <div className="notification-settings__section">
        <p className="notification-settings__section-desc">{t('notificationSettings.tableDescription')}</p>

        <div className="notification-settings__table">
          <div className="notification-settings__table-header">
            <span />
            <span className={`notification-settings__column-header${!smsAvailable ? ' notification-settings__column-header--disabled' : ''}`}>
              <span className="notification-settings__column-header-label">{t('notificationSettings.smsColumn')}</span>
              {!smsAvailable && (
                <span className="notification-settings__column-header-icon">
                  <span
                    className="notification-settings__tooltip"
                    data-tooltip={t('notificationSettings.phoneRequiredHint')}
                    aria-label={t('notificationSettings.phoneRequiredHint')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </span>
                </span>
              )}
            </span>
            <span className="notification-settings__column-header">
              <span className="notification-settings__column-header-label">{t('notificationSettings.emailColumn')}</span>
            </span>
          </div>

          {NOTIFICATION_ROWS.map((row) => (
            <div className="notification-settings__table-row" key={row.label}>
              <span className="notification-settings__row-label">{t(`notificationSettings.${row.label}`)}</span>
              {row.smsField ? renderSwitch(row.smsField, !smsAvailable) : <span />}
              {renderSwitch(row.emailField)}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default NotificationSettings;

